/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { DialogType, ITheme, Stack } from '@fluentui/react';
import { useOrganizationBranding } from 'features/Header/hooks';
import { ICallAction } from 'features/Rooms/RoomView/CallActions';
import { IRoomResource } from 'kits/apiKit3/legacy';
import SetTopicDialog from 'features/SetTopicDialog/SetTopicDialog';
import { IC } from 'components/SVGIcon';
import { ThreeDots } from 'kits/IconKit2';
import { useShowAttachmentPanel, useWindowWidth } from 'shared-state/display-options/hooks';
import { useLocalProfile } from 'shared-state/identity/hooks';
import { useCurrentOrgId, useIsVisitorAtLandingPage } from 'shared-state/location/hook';
import { actionBarStyles } from './CallActionBar.style';
import { ICallActionGroup } from './CallActionBar';
import DialogActions from './components/DialogActions';
import RoomHeader from './components/RoomHeader';
import TopicDisplay from './components/TopicDisplay';
import ActionGroup from './components/ActionGroup';
import { useTypeOfPersonalRoomTable } from 'features/Rooms/PersonalMeetingCorner/states/hooks';

export interface ICallActionBarViewProps {
    actions: ICallActionGroup[];
    onActionPressed: (a: ICallAction) => void;
    visible?: boolean;
    theme: ITheme;
    currentRoomId?: string;
    currentRoom?: IRoomResource;
    hideDialog: boolean;
    toggleHideDialog: () => void;
    dialogContentProps: {
        type: DialogType;
        title: string;
        closeButtonAriaLabel: string;
    };
    setToggleSetTopicDialog: (v: boolean) => void;
    showTopic: boolean;
    setShowTopic: React.Dispatch<React.SetStateAction<boolean>>;
    topic: string;
    showIconOnHover: boolean;
    setShowIconOnHover: React.Dispatch<React.SetStateAction<boolean>>;
    showCallOut: boolean;
    setShowCallOut: React.Dispatch<React.SetStateAction<boolean>>;
    topicVal: string;
    cameraIsMuted: boolean;
    micIsMuted: boolean;
    sharingIsOn: boolean;
    statsAreVisible: boolean;
    isLocalUserHandRaised: boolean;
    isChatVisible?: boolean;
}

const CallActionBarView: React.FC<ICallActionBarViewProps> = (props) => {
    const {
        theme,
        currentRoomId,
        currentRoom,
        hideDialog,
        toggleHideDialog,
        dialogContentProps,
        actions,
        onActionPressed,
        visible,
        setToggleSetTopicDialog,
        showTopic,
        topic,
        showIconOnHover,
        setShowIconOnHover,
        setShowCallOut,
        cameraIsMuted,
        micIsMuted,
        sharingIsOn,
        statsAreVisible,
        isLocalUserHandRaised,
        isChatVisible
    } = props;

    const { smallDevice, mediumDevice, smallLaptop } = useWindowWidth();
    const [isVisitorAtLandingPage] = useIsVisitorAtLandingPage();
    const [orgId] = useCurrentOrgId();
    const [branding] = useOrganizationBranding(orgId);
    const localProfile = useLocalProfile();
    const bgColor = branding?.orgColors[0];

    const [showAttachmentPanel] = useShowAttachmentPanel();
    const [typeOfPersonalRoomTable, setTypeOfPersonalRoomTable] = useTypeOfPersonalRoomTable();

    const [isAttachmentBarMousOver, setAttachmentBarMousOver] = useState(false);
    const [showHoverButton, setShowHoverButton] = useState(false);

    // Control hover button visibility
    useEffect(() => {
        const isPanelsOpen = !(showAttachmentPanel === 'closed' && !statsAreVisible);
        setShowHoverButton(!isPanelsOpen && !isAttachmentBarMousOver);
    }, [showAttachmentPanel, statsAreVisible, isAttachmentBarMousOver]);

    const isSmallLayout = smallDevice || mediumDevice;
    const isDialogVisible = hideDialog && isSmallLayout;

    return (
        <>
            {/* 🔲 Dialog for smaller devices */}
            <DialogActions
                hidden={isDialogVisible}
                toggleHideDialog={toggleHideDialog}
                dialogContentProps={dialogContentProps}
                actions={actions}
                onActionPressed={onActionPressed}
                micIsMuted={micIsMuted}
                cameraIsMuted={cameraIsMuted}
                sharingIsOn={sharingIsOn}
                statsAreVisible={statsAreVisible}
                isLocalUserHandRaised={isLocalUserHandRaised}
                isChatVisible={isChatVisible}
            />

            {/* 🧱 Main Stack Bar */}
            <Stack>
                <Stack className={actionBarStyles(theme) + (!visible ? ' reveal--on-hover' : '')} horizontal tokens={{ childrenGap: 8 }}>
                    <Stack grow horizontal verticalAlign="center" horizontalAlign={isSmallLayout ? 'center' : 'start'}>
                        <RoomHeader
                            currentRoomId={currentRoomId}
                            currentRoom={currentRoom}
                            isVisitorAtLandingPage={isVisitorAtLandingPage}
                            typeOfPersonalRoomTable={typeOfPersonalRoomTable}
                            setTypeOfPersonalRoomTable={setTypeOfPersonalRoomTable}
                        />

                        {/* 📝 Topic display for large devices */}
                        {!isSmallLayout && window.location.pathname.includes('rooms') && !window.location.pathname.includes('personal-') && (
                            <Stack style={{ margin: '0 10px', padding: '2px 5px', minWidth: '10%', maxWidth: '520px' }}>
                                <TopicDisplay
                                    showTopic={showTopic}
                                    topic={topic}
                                    showIconOnHover={showIconOnHover}
                                    setShowIconOnHover={setShowIconOnHover}
                                    setShowCallOut={setShowCallOut}
                                    setToggleSetTopicDialog={setToggleSetTopicDialog}
                                    localProfile={localProfile}
                                    bgColor={bgColor}
                                    isSmallDevice={smallDevice}
                                    isSmallLaptop={smallLaptop}
                                />
                            </Stack>
                        )}
                    </Stack>

                    {/* 📱 Render menu or buttons */}
                    {isSmallLayout ? (
                        <Stack style={{ cursor: smallLaptop ? 'pointer' : '' }}>
                            <IC variant={'custom'} size={22}>
                                <ThreeDots onClick={toggleHideDialog} />
                            </IC>
                        </Stack>
                    ) : (
                        actions.map((group, i) => (
                            <ActionGroup
                                key={group.key}
                                actionGroup={group}
                                index={i}
                                totalGroups={actions.length}
                                showHoverButton={showHoverButton}
                                isVisitorAtLandingPage={isVisitorAtLandingPage}
                                onMouseEnter={() => setAttachmentBarMousOver(true)}
                                onMouseLeave={() => setAttachmentBarMousOver(false)}
                                onActionPressed={onActionPressed}
                                micIsMuted={micIsMuted}
                                cameraIsMuted={cameraIsMuted}
                                sharingIsOn={sharingIsOn}
                                statsAreVisible={statsAreVisible}
                                isLocalUserHandRaised={isLocalUserHandRaised}
                                isChatVisible={isChatVisible}
                            />
                        ))
                    )}
                </Stack>

                {/* 📝 Topic display for small devices */}
                {isSmallLayout &&
                    (showTopic || topic.length > 0) &&
                    window.location.pathname.includes('rooms') &&
                    !window.location.pathname.includes('personal-') && (
                        <TopicDisplay
                            showTopic={showTopic}
                            topic={topic}
                            showIconOnHover={showIconOnHover}
                            setShowIconOnHover={setShowIconOnHover}
                            setShowCallOut={setShowCallOut}
                            setToggleSetTopicDialog={setToggleSetTopicDialog}
                            localProfile={localProfile}
                            bgColor={bgColor}
                            isSmallDevice={smallDevice}
                            isSmallLaptop={smallLaptop}
                        />
                    )}

                {/* 🔐 Set topic modal */}
                <SetTopicDialog />
            </Stack>
        </>
    );
};

export default CallActionBarView;
